import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import {
  Box,
  Stack,
  VStack,
  StackDivider,
  Heading,
  Text,
  Link,
  ButtonGroup,
  IconButton,
} from '@chakra-ui/react';
import { FaYoutube, FaFacebookSquare, FaTwitter } from 'react-icons/fa';
import AppleBadge from '../images/app-badge-apple.svg';
import AndroidBadge from '../images/app-badge-google.svg';

const LINKS = [
  {
    header: 'HOVER',
    links: [
      { linkText: 'Product', href: '/product' },
      { linkText: 'Pricing', href: '/pricing' },
      { linkText: 'Partners', href: '/partners' },
      {
        linkText: 'Home Improvement Professionals',
        href: '/exterior-contractors',
      },
      { linkText: 'Insurance Carriers & Carrier Partners', href: '/insurance' },
      { linkText: 'Homeowners', href: '/homeowners' },
      {
        linkText: 'Architects & Designers',
        href: '/measurement-app-architects',
      },
      { linkText: 'Tax Assessors', href: '/tax-assessors' },
    ],
  },
  {
    header: 'Company',
    links: [
      { linkText: 'About', href: '/about' },
      { linkText: 'Blog', href: '/blog' },
      { linkText: 'News', href: '/news' },
      { linkText: 'Jobs', href: '/jobs' },
    ],
  },
  {
    header: 'Resources',
    links: [
      { linkText: 'Support', href: 'https://help.hover.to/en/' },
      { linkText: 'Contact Us', href: '/contact' },
      { linkText: 'Developers', href: 'https://help.hover.to/en/' },
      { linkText: 'Trust Center', href: '/trust-center' },
    ],
  },
];

interface FooterHeadingProps {
  children: React.ReactNode;
}

const FooterHeading = ({ children }: FooterHeadingProps) => (
  <Heading
    as="h4"
    mb="4"
    fontSize="sm"
    fontWeight="bold"
    textTransform="uppercase"
    letterSpacing="wider"
  >
    {children}
  </Heading>
);

const Copyright = () => (
  <Text fontSize="sm" color="gray.400">
    &copy; {new Date().getFullYear()} HOVER, Inc.
  </Text>
);

const LinkGrid = () => (
  <Stack
    direction={['column', 'column', 'row']}
    spacing={{ base: '10', md: '18', lg: '28' }}
    justifyContent="center"
  >
    {LINKS.map(({ header, links }) => (
      <Box minW="130px" key={header}>
        <FooterHeading>{header}</FooterHeading>
        <Stack>
          {links.map(({ linkText, href }) => (
            <Link href={href} fontSize="sm" key={linkText} variant="gray">
              {linkText}
            </Link>
          ))}
        </Stack>
      </Box>
    ))}
  </Stack>
);

const SocialMediaLinks = () => (
  <ButtonGroup variant="ghost" color="gray.600">
    <IconButton
      as="a"
      aria-label="Facebook"
      href="https://www.facebook.com/hover3d/"
      icon={<FaFacebookSquare fontSize="20px" />}
    />
    <IconButton
      as="a"
      aria-label="Twitter"
      href="https://twitter.com/hover3d"
      icon={<FaTwitter fontSize="20px" />}
    />
    <IconButton
      as="a"
      aria-label="Youtube"
      href="https://www.youtube.com/user/Hover3D"
      icon={<FaYoutube fontSize="20px" />}
    />
  </ButtonGroup>
);

const MobileLinks = () => (
  <VStack justify="center">
    <Link href="https://apps.apple.com/us/app/hover-3d-building-visualization/id942568673">
      <AppleBadge alt="Badge apple" style={{ width: 135, height: 40 }} />
    </Link>
    <Link href="https://play.google.com/store/apps/details?id=to.hover.android.app&hl=en">
      <AndroidBadge alt="Badge google" style={{ width: 135, height: 40 }} />
    </Link>
    <Box maxW={24}>
      <Link
        href="https://www.aicpa.org/interestareas/frc/assuranceadvisoryservices/aicpasoc2report.html"
        target="_blank"
      >
        <StaticImage
          src="../images/21972-312_SOC_NonCPA.png"
          alt="SOC logo service organization"
        />
      </Link>
    </Box>
  </VStack>
);

const Footer = () => (
  <Box
    mx="auto"
    maxW="7xl"
    pt={['16', '20', '24']}
    pb="12"
    px={{ base: '4', md: '8' }}
  >
    <Stack spacing="10" divider={<StackDivider />}>
      <Stack
        justifyContent="center"
        alignItems={['center', 'center', 'unset']}
        textAlign={['center', 'center', 'unset']}
        direction={['column', 'column', 'row']}
        spacing={['10', '28']}
      >
        <MobileLinks />
        <LinkGrid />
      </Stack>
      <Stack
        direction={{ base: 'column-reverse', md: 'row' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Copyright />
        <Stack direction="row" alignItems="center">
          <Stack direction="row" mr="5">
            <Link href="/terms_of_use" fontSize="sm" mr="3" variant="gray">
              Terms
            </Link>
            <Link href="/privacy" fontSize="sm" mt="0" variant="gray">
              Privacy
            </Link>
          </Stack>
          <SocialMediaLinks />
        </Stack>
      </Stack>
    </Stack>
  </Box>
);

export default Footer;
